import React, { FC, MouseEventHandler, useEffect, useRef } from 'react';

interface Props {
    /** True only if the photo has the black and white filter applied */
    isBlackAndWhite: boolean;
    /** Thumbnail shown until the video's first frame decodes */
    posterUrl: string;
    /** Progressive MP4 URL to preview */
    previewUrl: string;
}

const handleContextMenu: MouseEventHandler = (event) => event.preventDefault();

/**
 * A silent, looping video preview that covers a grid item's thumbnail. Mounted only while the
 * preview is active, so an unhovered gallery never requests any video.
 */
const VideoGridItemPreview: FC<Props> = ({ isBlackAndWhite, posterUrl, previewUrl }) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const video = videoRef.current;

        if (!video) {
            return;
        }

        // React can drop the muted attribute on mount, and an unmuted video is not allowed to
        // autoplay, so set the property directly before asking it to play.
        video.muted = true;

        // Promise.resolve absorbs both a browser that blocks autoplay and jsdom's void play().
        void Promise.resolve(video.play()).catch(() => undefined);

        return () => {
            video.pause();
            // Dropping the source aborts any in-flight range request for the abandoned preview.
            video.removeAttribute('src');
            video.load();
        };
    }, [previewUrl]);

    return (
        <video
            ref={videoRef}
            data-testid="videoPreview"
            src={previewUrl}
            poster={posterUrl}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            controls={false}
            disablePictureInPicture
            draggable={false}
            // Decorative: the thumbnail's alt text already names the video, and the tile's controls
            // live in PhotoOverlay, so the preview is hidden from assistive tech and from tab order.
            aria-hidden="true"
            tabIndex={-1}
            onContextMenu={handleContextMenu}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: isBlackAndWhite ? 'grayscale(1)' : undefined,
                // Matches the thumbnail, so clicks fall through to PhotoOverlay.
                pointerEvents: 'none'
            }}
        />
    );
};

export default VideoGridItemPreview;
